import { useState } from 'react';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SeoHead from '@/components/feature/SeoHead';
import { brandMilestones, testimonials, aboutFaqItems } from '@/mocks/faq';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypotVal = (formData.get('website_alt') as string || '').trim();
    if (honeypotVal) {
      setFormStatus('success');
      form.reset();
      setTimeout(() => setFormStatus('idle'), 5000);
      return;
    }

    const formAction = 'https://readdy.ai/api/form/d9slqa58o1vn02ekuhl0';

    try {
      formData.delete('website_alt');
      const response = await fetch(formAction, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed: Record<string, unknown> | null = null;
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }

      if (response.ok && parsed && parsed.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = (parsed && typeof parsed === 'object'
          ? ((parsed as Record<string, unknown>).meta as Record<string, string>)?.message
            || (parsed as Record<string, string>).message
            || ((parsed as Record<string, unknown>).meta as Record<string, string>)?.detail
          : null) || responseText;
        setFormError(typeof serverMsg === 'string' ? serverMsg : '提交失败，请稍后再试');
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查您的网络连接后重试');
      setFormStatus('error');
    }
  };

  const organizationJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '蜜桃视频',
    url: 'https://mitaovideo.com',
    description: '蜜桃视频是您发现优质高清视频内容的首选平台，汇聚海量精选视频资源。',
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      email: 'contact@mitaovideo.com',
      availableLanguage: 'Chinese',
    },
  };

  const aboutFaqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: aboutFaqItems.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  };

  return (
    <>
      <SeoHead
        title="关于我们 - 蜜桃视频 | 品牌介绍与联系方式"
        description="了解蜜桃视频品牌故事、发展历程与核心团队。蜜桃视频是专注于精选高清视频内容的聚合平台，提供联系方式与合作咨询。"
        keywords="蜜桃视频,关于蜜桃视频,蜜桃视频品牌,蜜桃视频联系方式,视频平台"
        canonicalUrl="https://mitaovideo.com/about"
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(organizationJsonLd) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(aboutFaqJsonLd) }}
      />

      <div className="w-full px-4 md:px-6 pt-24 pb-12 md:pb-16 bg-background-50">
        <div className="max-w-4xl mx-auto">
          <div className="mb-6">
            <Breadcrumb items={[{ name: '首页', path: '/' }, { name: '关于我们' }]} />
          </div>

          <section className="mb-12 md:mb-16">
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-4">关于蜜桃视频</h1>
            <div className="flex flex-col lg:flex-row gap-8 items-start">
              <div className="flex-1">
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                  蜜桃视频成立于2024年，是一家专注于精选高清视频内容的聚合平台。我们的使命是为用户提供最高品质的视频内容体验，让每一位用户都能在这里发现属于自己的精彩世界。
                </p>
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                  在信息爆炸的时代，我们深知内容质量的重要性。蜜桃视频的团队由一群热爱视频内容的专业人士组成，每天从海量资源中精心筛选最具价值的内容，涵盖<strong>生活、娱乐、美食、旅行、科技、时尚</strong>六大核心类目，确保每一部视频都值得观看。
                </p>
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                  我们持续投入技术创新，采用领先的视频编码技术和CDN加速服务，致力于为用户提供<strong>高清画质</strong>和<strong>流畅加载</strong>的极致观影体验。无论您使用什么设备，都能享受丝滑的视频播放。
                </p>
              </div>
              <div className="w-full lg:w-80 flex-shrink-0">
                <div className="bg-background-100 rounded-lg p-5 border border-background-200/70">
                  <h3 className="text-base font-semibold text-foreground-950 mb-3">平台数据</h3>
                  <div className="grid grid-cols-2 gap-4">
                    {[
                      { label: '注册用户', value: '500万+' },
                      { label: '视频资源', value: '10万+' },
                      { label: '日活跃用户', value: '50万+' },
                      { label: '内容类目', value: '6大类' },
                    ].map((stat) => (
                      <div key={stat.label}>
                        <div className="text-xl md:text-2xl font-heading font-bold text-primary-500">{stat.value}</div>
                        <div className="text-xs text-foreground-500 mt-1">{stat.label}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="mb-12 md:mb-16">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-6">发展历程</h2>
            <div className="relative">
              <div className="absolute left-4 md:left-6 top-0 bottom-0 w-0.5 bg-primary-100"></div>
              <div className="flex flex-col gap-6">
                {brandMilestones.map((milestone, i) => (
                  <div key={i} className="relative pl-10 md:pl-14">
                    <div className="absolute left-2.5 md:left-4.5 top-1.5 w-3 h-3 rounded-full bg-primary-500 border-2 border-background-50"></div>
                    <div className="bg-background-50 border border-background-200/70 rounded-lg p-4 md:p-5">
                      <span className="text-xs font-semibold text-primary-500 bg-primary-50 px-2 py-0.5 rounded-full">
                        {milestone.year}
                      </span>
                      <h3 className="text-sm md:text-base font-semibold text-foreground-950 mt-2 mb-1">{milestone.title}</h3>
                      <p className="text-xs md:text-sm text-foreground-600">{milestone.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section id="testimonials" className="mb-12 md:mb-16">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-6">用户口碑</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {testimonials.map((t, i) => (
                <div key={i} className="bg-background-50 border border-background-200/70 rounded-lg p-4 md:p-5">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-full bg-accent-100 text-accent-600 flex items-center justify-center text-sm font-semibold">
                      {t.avatar}
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground-900">{t.name}</div>
                      <div className="text-xs text-foreground-500">{t.role}</div>
                    </div>
                  </div>
                  <div className="flex gap-0.5 mb-2">
                    {Array.from({ length: t.rating }).map((_, j) => (
                      <i key={j} className="ri-star-fill text-xs text-accent-500"></i>
                    ))}
                  </div>
                  <p className="text-xs md:text-sm text-foreground-600 leading-relaxed">{t.content}</p>
                </div>
              ))}
            </div>
          </section>

          <section id="contact" className="mb-12 md:mb-16">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-6">联系我们</h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div>
                <div className="flex flex-col gap-4">
                  {[
                    { icon: 'ri-mail-line', label: '电子邮箱', value: 'contact@mitaovideo.com' },
                    { icon: 'ri-phone-line', label: '客服电话', value: '400-888-6666' },
                    { icon: 'ri-map-pin-line', label: '公司地址', value: '北京市朝阳区望京科技园A座' },
                    { icon: 'ri-time-line', label: '工作时间', value: '周一至周五 9:00 - 18:00' },
                  ].map((item) => (
                    <div key={item.label} className="flex items-start gap-3">
                      <div className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-50 text-primary-500 flex-shrink-0">
                        <i className={item.icon}></i>
                      </div>
                      <div>
                        <div className="text-xs text-foreground-500">{item.label}</div>
                        <div className="text-sm font-medium text-foreground-900">{item.value}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <div className="bg-background-50 border border-background-200/70 rounded-lg p-5">
                  <h3 className="text-base font-semibold text-foreground-950 mb-4">发送消息</h3>
                  {formStatus === 'success' ? (
                    <div className="text-center py-8">
                      <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-full bg-accent-50 text-accent-500">
                        <i className="ri-check-line text-xl"></i>
                      </div>
                      <p className="text-sm font-medium text-foreground-900 mb-1">消息已发送！</p>
                      <p className="text-xs text-foreground-500">感谢您的留言，我们会尽快回复。</p>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} className="flex flex-col gap-3" data-readdy-form="">
                      <input
                        type="text"
                        name="name"
                        placeholder="您的姓名"
                        required
                        className="w-full px-3 py-2 text-sm border border-background-200/70 rounded-md bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                      />
                      <input
                        type="email"
                        name="email"
                        placeholder="您的邮箱"
                        required
                        className="w-full px-3 py-2 text-sm border border-background-200/70 rounded-md bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                      />
                      <input
                        type="text"
                        name="subject"
                        placeholder="主题"
                        className="w-full px-3 py-2 text-sm border border-background-200/70 rounded-md bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                      />
                      <textarea
                        name="message"
                        placeholder="请输入您的留言内容"
                        rows={4}
                        required
                        maxLength={500}
                        className="w-full px-3 py-2 text-sm border border-background-200/70 rounded-md bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors resize-none"
                      ></textarea>
                      <input
                        type="text"
                        name="website_alt"
                        tabIndex={-1}
                        autoComplete="off"
                        aria-hidden="true"
                        readOnly
                        className="visually-hidden-field"
                      />
                      {formStatus === 'error' && (
                        <p className="text-xs text-red-500">{formError}</p>
                      )}
                      <button
                        type="submit"
                        className="w-full px-4 py-2.5 bg-primary-500 text-background-50 rounded-md text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                      >
                        发送消息
                      </button>
                    </form>
                  )}
                </div>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-6">常见问题</h2>
            <div className="flex flex-col gap-3">
              {aboutFaqItems.map((item, i) => (
                <details key={i} className="group border border-background-200/70 rounded-lg overflow-hidden cursor-pointer">
                  <summary className="flex items-center justify-between p-4 bg-background-50 hover:bg-background-100 transition-colors">
                    <span className="text-sm font-medium text-foreground-900 pr-4">{item.question}</span>
                    <i className="ri-arrow-down-s-line text-foreground-500 group-open:rotate-180 transition-transform duration-200 flex-shrink-0"></i>
                  </summary>
                  <div className="px-4 pb-4">
                    <p className="text-sm text-foreground-600 leading-relaxed">{item.answer}</p>
                  </div>
                </details>
              ))}
            </div>
          </section>
        </div>
      </div>
    </>
  );
}